import store from 'app/store';
import {
  AttomDataType,
  AttributeCodeType,
  AttributeType,
  LevelType,
  LevelTypeType
} from 'data/types';
import { getAttomSquareFootage } from 'utils/coverage-square-footage';
import { isNotEmpty } from 'utils/helpers';
import { createLevel } from 'utils/structure';

export const HYBRID_AUTOBUILD_TUNABLES = {
  INCLUDE_ATTIC_BY_DEFAULT: true,
  DEFAULT_ATTIC_MAX_STORIES: 2,
  CONDO_PROP_TYPE_PATTERN: /condo|apartment/i,

  TWO_STORY_SQFT_THRESHOLD: 1800,
  MIN_STORIES: 1,
  MAX_STORIES: 3,
  MIN_SQUARE_FOOTAGE: 100,

  CRAWLSPACE_CEILING_HEIGHT: '4',
  DEFAULT_CEILING_HEIGHT: '8',

  DEFAULT_LEVEL_CEILING_TYPE: 'Flat',
  LEVEL_HVAC_CONDITIONED: 'Primary',
  LEVEL_HVAC_UNCONDITIONED: 'None',

  WINDOW_WIDTH_FEET: 3,
  WINDOW_HEIGHT_FEET: 4
} as const;

export type HybridAutobuildSource = 'setting' | 'attom' | 'default';

export type HybridDerivedValue<T> = {
  value: T;
  source: HybridAutobuildSource;
};

export type HybridAutobuildDerivation = {
  squareFootage: HybridDerivedValue<number> | null;
  stories: HybridDerivedValue<number>;
  basement: HybridDerivedValue<'none' | 'unfinished' | 'finished'>;
  crawlspace: HybridDerivedValue<boolean>;
  attic: HybridDerivedValue<boolean>;
};

export type HybridAutobuildLevelSpec = {
  type: LevelTypeType;
  label: string;
  conditioned: boolean;
};

export type HybridAutobuildPlan = {
  levels: HybridAutobuildLevelSpec[];
  derivation: HybridAutobuildDerivation;
};

export type HybridAutobuildInputs = {
  attomData: AttomDataType | null;
  squareFootage?: unknown;
  numberOfFloors?: unknown;
  foundationType?: unknown;
};

function toPositiveNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function toPositiveInt(value: unknown): number | null {
  const n = toPositiveNumber(value);
  if (n === null) return null;
  const rounded = Math.round(n);
  return rounded >= 1 ? rounded : null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const CONDITIONED_LEVEL_LABELS = ['First Floor', 'Second Floor', 'Third Floor'];

function deriveSquareFootage(
  inputs: HybridAutobuildInputs
): HybridDerivedValue<number> | null {
  const T = HYBRID_AUTOBUILD_TUNABLES;
  const sqftSetting = toPositiveNumber(inputs.squareFootage);

  let squareFootage: HybridDerivedValue<number> | null = null;
  if (sqftSetting !== null) {
    squareFootage = { value: Math.round(sqftSetting), source: 'setting' };
  } else {
    const sqftAttom = getAttomSquareFootage(inputs.attomData);
    if (sqftAttom !== null) {
      squareFootage = { value: Math.round(sqftAttom), source: 'attom' };
    }
  }
  if (squareFootage && squareFootage.value < T.MIN_SQUARE_FOOTAGE) {
    return null;
  }
  return squareFootage;
}

export function deriveHybridAutobuildPlan(
  inputs: HybridAutobuildInputs
): HybridAutobuildPlan {
  const T = HYBRID_AUTOBUILD_TUNABLES;
  const attom = inputs.attomData;
  const squareFootage = deriveSquareFootage(inputs);

  const storiesSetting = toPositiveInt(inputs.numberOfFloors);
  const storiesAttom = toPositiveInt(attom?.building?.summary?.levels);
  let stories: HybridDerivedValue<number>;
  if (storiesSetting !== null) {
    stories = {
      value: clamp(storiesSetting, T.MIN_STORIES, T.MAX_STORIES),
      source: 'setting'
    };
  } else if (storiesAttom !== null) {
    stories = {
      value: clamp(storiesAttom, T.MIN_STORIES, T.MAX_STORIES),
      source: 'attom'
    };
  } else if (squareFootage) {
    stories = {
      value: squareFootage.value < T.TWO_STORY_SQFT_THRESHOLD ? 1 : 2,
      source: 'default'
    };
  } else {
    stories = { value: 1, source: 'default' };
  }

  const foundationSetting =
    typeof inputs.foundationType === 'string' && inputs.foundationType !== ''
      ? inputs.foundationType
      : null;
  const bsmtType = attom?.building?.interior?.bsmtType ?? null;
  const attomHasBasement =
    toPositiveNumber(attom?.building?.interior?.bsmtSize) !== null ||
    bsmtType !== null;
  const basementFinished =
    typeof bsmtType === 'string' &&
    /FINISH/i.test(bsmtType) &&
    !/UNFINISH/i.test(bsmtType);
  let basement: HybridDerivedValue<'none' | 'unfinished' | 'finished'>;
  if (foundationSetting === 'Basement') {
    basement = {
      value: basementFinished ? 'finished' : 'unfinished',
      source: 'setting'
    };
  } else if (foundationSetting !== null) {
    basement = { value: 'none', source: 'setting' };
  } else if (attomHasBasement) {
    basement = {
      value: basementFinished ? 'finished' : 'unfinished',
      source: 'attom'
    };
  } else {
    basement = { value: 'none', source: 'default' };
  }

  const attomCrawlspace = /crawl/i.test(
    attom?.building?.construction?.foundationType ?? ''
  );
  let crawlspace: HybridDerivedValue<boolean>;
  if (basement.value !== 'none') {
    crawlspace = { value: false, source: basement.source };
  } else if (/crawl/i.test(foundationSetting ?? '')) {
    crawlspace = { value: true, source: 'setting' };
  } else if (foundationSetting !== null) {
    crawlspace = { value: false, source: 'setting' };
  } else if (attomCrawlspace) {
    crawlspace = { value: true, source: 'attom' };
  } else {
    crawlspace = { value: false, source: 'default' };
  }

  const atticSizeAttom = toPositiveNumber(attom?.building?.size?.atticSize);
  const condoLike = T.CONDO_PROP_TYPE_PATTERN.test(
    `${attom?.summary?.propType ?? ''} ${attom?.summary?.propSubType ?? ''}`
  );
  let attic: HybridDerivedValue<boolean>;
  if (atticSizeAttom !== null) {
    attic = { value: true, source: 'attom' };
  } else {
    attic = {
      value:
        T.INCLUDE_ATTIC_BY_DEFAULT &&
        stories.value <= T.DEFAULT_ATTIC_MAX_STORIES &&
        !condoLike,
      source: 'default'
    };
  }

  const derivation: HybridAutobuildDerivation = {
    squareFootage,
    stories,
    basement,
    crawlspace,
    attic
  };

  const levels: HybridAutobuildLevelSpec[] = [];

  if (basement.value === 'finished') {
    levels.push({
      type: 'basement',
      label: 'Basement',
      conditioned: true
    });
  } else if (basement.value === 'unfinished') {
    levels.push({
      type: 'basement',
      label: 'Basement',
      conditioned: false
    });
  }

  if (crawlspace.value) {
    levels.push({
      type: 'crawlspace',
      label: 'Crawlspace',
      conditioned: false
    });
  }

  if (stories.value === 1) {
    levels.push({
      type: 'level',
      label: 'Main Level',
      conditioned: true
    });
  } else {
    for (let floorIdx = 0; floorIdx < stories.value; floorIdx += 1) {
      levels.push({
        type: 'level',
        label: CONDITIONED_LEVEL_LABELS[floorIdx],
        conditioned: true
      });
    }
  }

  if (attic.value) {
    levels.push({
      type: 'attic',
      label: 'Attic',
      conditioned: false
    });
  }

  return { levels, derivation };
}

function withAttributeValue(
  attributes: AttributeType[],
  code: AttributeCodeType,
  value: unknown
): AttributeType[] {
  return attributes.map((a) => (a.code === code ? { ...a, value } : a));
}

function getAttributeValueByCode(
  attributes: AttributeType[],
  code: AttributeCodeType
): unknown {
  return attributes.find((a) => a.code === code)?.value;
}

function hasEmptyAttributeValue(
  attributes: AttributeType[],
  code: AttributeCodeType
): boolean {
  return !isNotEmpty(getAttributeValueByCode(attributes, code));
}

export function buildStructureFromPlan(
  plan: HybridAutobuildPlan
): LevelType[] | null {
  const { items } = store.getState().data;
  const { projectSettings } = store.getState().project;

  const levelTypesNeeded = [...new Set(plan.levels.map((l) => l.type))];
  if (
    levelTypesNeeded.some((type) => !items.some((item) => item.code === type))
  ) {
    return null;
  }

  const levels: LevelType[] = [];
  for (const levelSpec of plan.levels) {
    const level = createLevel(levelSpec.type, {
      attributesToInherit: projectSettings,
      data: {
        label: levelSpec.label,
        conditioned: levelSpec.conditioned
      }
    });
    if (!level) return null;

    if (levelSpec.type === 'crawlspace') {
      level.attributes = withAttributeValue(
        level.attributes,
        'ceiling_height',
        HYBRID_AUTOBUILD_TUNABLES.CRAWLSPACE_CEILING_HEIGHT
      );
    }

    if (hasEmptyAttributeValue(level.attributes, 'ceiling_height')) {
      level.attributes = withAttributeValue(
        level.attributes,
        'ceiling_height',
        HYBRID_AUTOBUILD_TUNABLES.DEFAULT_CEILING_HEIGHT
      );
    }

    if (hasEmptyAttributeValue(level.attributes, 'level_ceiling_type')) {
      level.attributes = withAttributeValue(
        level.attributes,
        'level_ceiling_type',
        HYBRID_AUTOBUILD_TUNABLES.DEFAULT_LEVEL_CEILING_TYPE
      );
    }
    const levelHvac = levelSpec.conditioned
      ? HYBRID_AUTOBUILD_TUNABLES.LEVEL_HVAC_CONDITIONED
      : HYBRID_AUTOBUILD_TUNABLES.LEVEL_HVAC_UNCONDITIONED;
    (['heating', 'cooling'] as const).forEach((code) => {
      if (hasEmptyAttributeValue(level.attributes, code)) {
        level.attributes = withAttributeValue(
          level.attributes,
          code,
          levelHvac
        );
      }
    });

    level.sections = [];
    levels.push(level);
  }
  return levels;
}
